import { createSignal, createMemo, Show, onCleanup } from 'solid-js';
import { Button } from '@bz/ui';
import SelectLable from '@bz/wallet/src/components/ui/SelectLable';
import { createWritableMemo } from '@solid-primitives/memo';

type ExclusionPeriod = 'day' | 'week' | 'permanent';

type ExclusionState = ApiSchemas['SelfExclusionVO'];

const reasonOptions = [
  { value: 'gambling_problem', label: t('I have a gambling problem') },
  { value: 'financial', label: t('Financial reasons') },
  { value: 'mental_health', label: t('Mental health reasons') },
  { value: 'other', label: t('Other') },
];

function formatCountdown(ms: number): string {
  const totalSecs = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSecs / 86400);
  const hours = Math.floor((totalSecs % 86400) / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return parts.join(' ');
}

function periodDurationMs(period: ExclusionPeriod): number | null {
  if (period === 'day') return 24 * 60 * 60 * 1000;
  if (period === 'week') return 7 * 24 * 60 * 60 * 1000;
  return null;
}

function periodLabel(period: string): string {
  if (period === 'day') return t('1-Day');
  if (period === 'week') return t('7-Day');
  return t('Permanent');
}

// ── Setup state ──────────────────────────────────────────────────────────────

function SetupView(props: { onActivate: (state: ExclusionState) => void }) {
  const [selected, setSelected] = createSignal<ExclusionPeriod>('day');
  const [reason, setReason] = createSignal<{ label: string; value: string }>();
  const [agreed, setAgreed] = createSignal(['0']);

  const selectedReasonLabel = createMemo(() => reason()?.label ?? t('Select a reason (optional)'));
  const AddSelfExclusion = useAction(post.createUserInsuranceAddSelfExclusion);
  const handleConfirm = () => {
    if (!agreed()) return;
    pop
      .confirm(
        () => (
          <div class="pt-4">
            {t('Are you sure you want to self-exclude? This action cannot be reversed until the period expires.')}
          </div>
        ),
        {
          confirm: t('Yes, Confirm'),
          cancel: t('Cancel'),
        }
      )
      .then(async (ok) => {
        if (!ok) return;
        await AddSelfExclusion({
          type: selected(),
          reason: reason()?.value!,
        });
        revalidate([get.getUserInsuranceGetSelfExclusion.key, get.getUserInsuranceGetInsuranceStatus.key]);
        toast(t('Self-exclusion activated'));
      });
  };

  const periods: { value: ExclusionPeriod; title: string; sub: string }[] = [
    { value: 'day', title: t('1 Day'), sub: t('24 hours') },
    { value: 'week', title: t('1 Week'), sub: t('7 days') },
    { value: 'permanent', title: t('Permanent'), sub: t('Indefinite') },
  ];

  return (
    <div class=" flex flex-col gap-4 p-4 rounded-xl shadow-[0px_2px_24px_0px_rgba(10,15,19,0.05)] w-full">
      <span class="font-semibold text-base text-neutral-main1 leading-[1.5]">{t('Self Exclusion')}</span>

      {/* Important Information */}
      <div class="bg-alpha-function-orange-a5 border border-orange flex flex-col gap-3 px-4 py-4 rounded-lg w-full">
        <span class="font-semibold text-base text-neutral-main1 leading-[1.5]">{t('Important Information')}</span>
        <p class="font-semibold text-sm text-neutral-main1 leading-[1.5]">
          {t(
            'Self-exclusion will restrict your access to all gambling services. This decision cannot be reversed until the selected period expires.'
          )}
        </p>
      </div>

      {/* Period selector */}
      <div class="flex flex-col gap-2 w-full">
        <span class="font-semibold text-base text-neutral-main1 leading-[1.5]">{t('Exclusion Period')}</span>
        <div class="grid grid-cols-3 gap-3 w-full">
          {periods.map((p) => (
            <button
              class={`bg-layer5-card flex flex-col gap-1 items-center justify-center p-4 rounded-lg border transition-colors ${
                selected() === p.value ? 'border-brand' : 'border-main2'
              }`}
              onClick={() => setSelected(p.value)}
            >
              <span
                class={`font-semibold text-lg leading-[1.5] ${selected() === p.value ? 'text-brand-default' : 'text-neutral-main1'}`}
              >
                {p.title}
              </span>
              <span
                class={`font-semibold text-base leading-[1.5] ${selected() === p.value ? 'text-brand-default' : 'text-neutral-main3'}`}
              >
                {p.sub}
              </span>
            </button>
          ))}
        </div>
      </div>
      <Select
        label={<SelectLable title={t('Exclusion Reason(Optional)')} content={selectedReasonLabel()} />}
        bind={[reason, (v) => setReason(v)]}
        optional
        placeholder={t('Select a reason (optional)')}
        options={reasonOptions}
      />

      {/* Acknowledgement checkbox */}
      <div class="border border-main2 flex gap-3 items-start px-3 py-3 rounded-lg w-full">
        <Checkbox value={'0'} bind={[agreed, setAgreed]} />
        <p class="font-semibold text-sm text-neutral-main2 leading-[1.5]">
          {t(
            'I understand that by proceeding with self-exclusion, I will be restricted from accessing all gambling services for the selected period. I acknowledge that this decision is voluntary and cannot be reversed until the exclusion period has expired.'
          )}
        </p>
      </div>

      {/* Confirm button */}
      <div class="flex items-center w-full">
        <Button
          type="brand"
          class={`h-11 min-w-[200px] rounded-full font-semibold text-[16px] text-neutral-alw-white leading-[1.2] bg-red border-0 hover:!bg-red ${!agreed() ? 'opacity-40' : ''}`}
          disabled={!agreed()[0]}
          onClick={handleConfirm}
        >
          {t('Confirm Self Exclusion')}
        </Button>
      </div>
    </div>
  );
}

// ── Active timed exclusion state ─────────────────────────────────────────────

function ActiveTimedView(props: { state: ExclusionState }) {
  const [remaining, setRemaining] = createWritableMemo(() =>
    props.state.endTime ? props.state.endTime - props.state.currentTime : 0
  );
  const timer = setInterval(() => setRemaining(remaining() - 1000), 1000);
  onCleanup(() => clearInterval(timer));
  const countdownText = createMemo(
    () => `${periodLabel(props.state.type)} ${t('Cooldown')} - ${formatCountdown(remaining())} ${t('remaining')}`
  );

  return (
    <div class="bg-layer4-card flex flex-col gap-4 p-4 rounded-xl shadow-[0px_2px_24px_0px_rgba(10,15,19,0.05)] w-full">
      <span class="font-semibold text-base text-neutral-main1 leading-[1.5]">{t('Self Exclusion')}</span>
      <p class="font-semibold text-sm text-neutral-main1 leading-[1.5]">
        {t(
          'Initiate the automated self-exclusion process by establishing a limit, and you will be temporarily barred from accessing your account for the selected period. Please be aware that during this time, you will not have access to any products on the website, and any remaining balances will also be unavailable.'
        )}
      </p>
      <div class="flex items-center w-full">
        <Button type="second" class="rounded-lg">
          <span class="font-semibold text-[16px] opacity-40 text-neutral-main1 leading-[1.2] text-center">
            {countdownText()}
          </span>
        </Button>
      </div>
    </div>
  );
}

// ── Permanent exclusion state ─────────────────────────────────────────────────

function PermanentView() {
  return (
    <div class="bg-layer4-card flex flex-col gap-4 p-4 rounded-xl shadow-[0px_2px_24px_0px_rgba(10,15,19,0.05)] w-full">
      <span class="font-semibold text-base text-neutral-main1 leading-[1.5]">{t('Self Exclusion')}</span>
      <p class="font-semibold text-sm text-neutral-main1 leading-[1.5]">
        {t(
          'Initiate the automated self-exclusion process by establishing a limit, and you will be temporarily barred from accessing your account for the selected period. Please be aware that during this time, you will not have access to any products on the website, and any remaining balances will also be unavailable.'
        )}
      </p>
      <div class="bg-alpha-function-red-a10 flex items-center p-3 rounded-[4px] w-full">
        <p class="font-semibold text-sm text-neutral-main1 leading-[1.5]">
          {t('Account permanently frozen, cannot be revoked')}
        </p>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export const route = {
  info: {
    login: true,
  },
} satisfies RouteDefinition;
export default function SelfExclusion() {
  const selfExclusion = createAsync(() => get.getUserInsuranceGetSelfExclusion());
  const [exclusionState, setExclusionState] = createWritableMemo<ExclusionState | undefined>(() => selfExclusion());

  return (
    // <Suspense fallback={<div class="flex items-center justify-center w-full py-10"><Loading /></div>}>
    // </Suspense>
    <div class="p-4">
      <Show when={exclusionState()} fallback={<SetupView onActivate={(s) => setExclusionState(s)} />}>
        {(state) => (
          <Show when={state().type !== 'permanent'} fallback={<PermanentView />}>
            <ActiveTimedView state={state()} />
          </Show>
        )}
      </Show>
    </div>
  );
}
